import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaCakeCandles,
  FaCartShopping,
  FaEye,
  FaCircleCheck,
  FaTruck,
  FaBullhorn,
  FaFilter,
  FaMagnifyingGlass,
  FaPlay,
  FaPlus,
  FaMinus,
  FaCreditCard,
  FaGlobe,
  FaBan,
  FaTriangleExclamation,
  FaCrown,
} from "react-icons/fa6";
import { SecondaryButton, DangerButton } from "@/components/Buttons.jsx";
import Badge from "@/components/Badges.jsx";
import InlineLoader from "@/components/InlineLoader.jsx";
import {
  getCustomerByEmail,
  getCustomerActivityHits,
  getCustomerActivityEmails,
  banCustomer,
  unbanCustomer,
} from "@/api/customers.js";
import { getOrdersByCustomer } from "@/api/orders.js";
import CanView from "@/components/CanView";
import PaginationNumeric from "@/components/PaginationNumeric.jsx";
import PastOrderModal from "@/partials/pastOrders/PastOrderModal.jsx";
import {
  OrderStatusInverseEnum,
  statusColor,
  formatPhoneNumber,
} from "@/utils/Utils.js";
import DateSelect from "@/components/DateSelect.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import { addToast } from "@/store/app/action.js";

// Function to get icon for activity event type
const getActivityIcon = (eventType) => {
  switch (eventType) {
    case "Viewed Product":
      return <FaEye className="text-blue-500" />;
    case "Add To Cart":
      return <FaCartShopping className="text-green-500" />;
    case "Removed From Cart":
      return <FaMinus className="text-red-500" />;
    case "Checkout Started":
      return <FaCircleCheck className="text-purple-500" />;
    case "Order Placed":
      return <FaCreditCard className="text-indigo-500" />;
    case "Order Delivered":
      return <FaTruck className="text-green-500" />;
    case "Order Picked Up":
      return <FaCircleCheck className="text-green-500" />;
    case "Order Canceled":
      return <FaMinus className="text-red-500" />;
    case "Session Started":
      return <FaPlay className="text-orange-500" />;
    case "Updated Quantity":
      return <FaPlus className="text-yellow-500" />;
    default:
      return <FaGlobe className="text-slate-500" />;
  }
};

// Function to get icon for email event type
const getEmailIcon = (eventType) => {
  switch (eventType) {
    case "blast":
      return <FaBullhorn className="text-indigo-500" />;
    case "Email":
      return <FaEnvelope className="text-blue-500" />;
    case "reengagement":
      return <FaEnvelope className="text-purple-500" />;
    case "cart_abandonment":
    case "cart_abandoment":
      return <FaCartShopping className="text-orange-500" />;
    case "welcome":
      return <FaEnvelope className="text-green-500" />;
    case "birthday":
      return <FaCakeCandles className="text-pink-500" />;
    default:
      return <FaEnvelope className="text-slate-500" />;
  }
};

// Mock data for now
const mockCustomer = {
  email: "mike.smith@example.com",
  first_name: "Mike",
  last_name: "Smith",
  mobile: "+1 (555) 123-4567",
  birthday: "1985-08-22",
  total_spent: 1250.75,
  order_count: 8,
  created_at: "2023-01-15",
};

const mockDeliveries = [
  {
    id: 1,
    type: "email_broadcast",
    title: "New Arrivals Newsletter",
    sent_date: "2024-01-14T09:00:00Z",
    status: "Delivered",
    opened: true,
    clicked: false,
  },
  {
    id: 2,
    type: "campaign",
    title: "Weekend Sale Campaign",
    sent_date: "2024-01-10T14:00:00Z",
    status: "Delivered",
    opened: true,
    clicked: true,
  },
  {
    id: 3,
    type: "email_broadcast",
    title: "Premium Cigar Collection Launch",
    sent_date: "2024-01-08T11:00:00Z",
    status: "Delivered",
    opened: false,
    clicked: false,
  },
];

const CustomerDetail = ({
  modalMode = false,
  customerEmail = null,
  onClose = null,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use customerEmail prop if in modal mode, otherwise use URL param
  const customerId = modalMode ? customerEmail : id;
  const { selectedLocation, selectedMerchant, user, merchants } = useSelector(
    (state) => state.app
  );
  const [customer, setCustomer] = useState(mockCustomer);
  const [orders, setOrders] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesTotalPages, setActivitiesTotalPages] = useState(0);
  const [emails, setEmails] = useState([]);
  const [emailsPage, setEmailsPage] = useState(1);
  const [emailsTotalPages, setEmailsTotalPages] = useState(0);
  const [selectedLocationForOrders, setSelectedLocationForOrders] =
    useState(null);
  const [selectedMerchantForOrders, setSelectedMerchantForOrders] =
    useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLimit, setOrdersLimit] = useState(10);
  const [ordersTotalPages, setOrdersTotalPages] = useState(0);
  const [ordersTotalRecords, setOrdersTotalRecords] = useState(0);
  const [isBasicInfoCollapsed, setIsBasicInfoCollapsed] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(new Date(0)); // All Time
  const [endDate, setEndDate] = useState(new Date());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [unbanModalOpen, setUnbanModalOpen] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banningCustomer, setBanningCustomer] = useState(false);
  const [unbanningCustomer, setUnbanningCustomer] = useState(false);
  const [banGlobally, setBanGlobally] = useState(false);

  // Function to handle activity link clicks
  const handleActivityLinkClick = (link) => {
    if (!link) return;

    if (link.startsWith("orderModal://")) {
      const orderId = link.replace("orderModal://", "");
      setSelectedOrderId(orderId);
      setOrderModalOpen(true);
    } else if (link.startsWith("http")) {
      window.open(link, "_blank");
    }
  };

  const fetchCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Reset activities and emails when customer changes
      setActivities([]);
      setEmails([]);
      setActivitiesPage(1);
      setEmailsPage(1);
      setEmailsTotalPages(0);

      const customerData = await getCustomerByEmail(customerId);
      setCustomer(customerData);

      // Fetch customer activity if mautic_id exists
      if (customerData.mautic_id) {
        try {
          // Fetch hits for activity panel
          const hitsData = await getCustomerActivityHits(
            customerData.mautic_id,
            1,
            25
          );
          const hitsArray = Array.isArray(hitsData)
            ? hitsData
            : Array.isArray(hitsData.data)
            ? hitsData.data
            : [];
          setActivities(hitsArray);
          setActivitiesTotalPages(hitsData.pagination?.total_pages || 0);

          // Fetch emails for emails panel
          const emailsData = await getCustomerActivityEmails(
            customerData.mautic_id,
            1,
            25
          );
          const emailsArray = Array.isArray(emailsData)
            ? emailsData
            : Array.isArray(emailsData.data)
            ? emailsData.data
            : [];
          setEmails(emailsArray);
          setEmailsTotalPages(emailsData.pagination?.total_pages || 0);
        } catch (activityErr) {
          // Reset to empty arrays on error
          setActivities([]);
          setEmails([]);
          setActivitiesTotalPages(0);
          setEmailsTotalPages(0);
        }
      } else {
        // No mautic_id, reset to empty arrays
        setActivities([]);
        setEmails([]);
        setActivitiesTotalPages(0);
        setEmailsTotalPages(0);
      }

      // TODO: Fetch orders and deliveries data
      // For now, keep using mock data for these
    } catch (err) {
      setError("Failed to load customer data");
      // Keep using mock data as fallback
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Function to handle ban customer
  const handleBanCustomer = async () => {
    if (!banReason.trim()) {
      dispatch(
        addToast({
          type: "warning",
          message: "Please provide a reason for banning this customer.",
          timer: 5,
        })
      );
      return;
    }

    try {
      setBanningCustomer(true);

      // Call the API to ban the customer (pass global flag if user is saucey_admin)
      await banCustomer(customer.id, banReason, banGlobally);

      // Close modal and reset form
      setBanModalOpen(false);
      setBanReason("");
      setBanGlobally(false);

      // Show success toast
      dispatch(
        addToast({
          type: "success",
          message: "Customer has been banned successfully.",
          timer: 5,
        })
      );

      // Refetch customer data to reflect the ban status
      await fetchCustomerData();
    } catch (error) {
      console.error("Error banning customer:", error);

      // Show error toast
      dispatch(
        addToast({
          type: "error",
          message: "Failed to ban customer. Please try again.",
          timer: 5,
        })
      );
    } finally {
      setBanningCustomer(false);
    }
  };

  // Function to handle unban customer
  const handleUnbanCustomer = async () => {
    try {
      setUnbanningCustomer(true);

      // Call the API to unban the customer
      await unbanCustomer(customer.id);

      // Close modal
      setUnbanModalOpen(false);

      // Show success toast
      dispatch(
        addToast({
          type: "success",
          message: "Customer has been unbanned successfully.",
          timer: 5,
        })
      );

      // Refetch customer data to reflect the unban status
      await fetchCustomerData();
    } catch (error) {
      console.error("Error unbanning customer:", error);

      // Show error toast
      dispatch(
        addToast({
          type: "error",
          message: "Failed to unban customer. Please try again.",
          timer: 5,
        })
      );
    } finally {
      setUnbanningCustomer(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId, fetchCustomerData]);

  // Reset location and merchant selection when customer ID changes
  useEffect(() => {
    if (customerId && selectedLocation?.id) {
      setSelectedLocationForOrders(selectedLocation.id);
      setSelectedMerchantForOrders(null);
      setOrdersPage(1); // Reset to first page
    } else if (customerId && user.type === "saucey_admin") {
      // For admin users, default to "All Locations" if no specific location is selected
      setSelectedLocationForOrders(null);
      setSelectedMerchantForOrders(null);
      setOrdersPage(1);
    }
  }, [customerId, selectedLocation?.id, user.type]);

  // Function to fetch activities with pagination
  const fetchActivities = async () => {
    if (!customer.mautic_id) return;

    try {
      const hitsData = await getCustomerActivityHits(
        customer.mautic_id,
        activitiesPage,
        25
      );
      const hitsArray = Array.isArray(hitsData)
        ? hitsData
        : Array.isArray(hitsData.data)
        ? hitsData.data
        : [];
      setActivities(hitsArray);
      setActivitiesTotalPages(hitsData.pagination?.total_pages || 0);
    } catch (error) {}
  };

  // Function to fetch emails with pagination
  const fetchEmails = async () => {
    if (!customer.mautic_id) return;

    try {
      const emailsData = await getCustomerActivityEmails(
        customer.mautic_id,
        emailsPage,
        25
      );
      const emailsArray = Array.isArray(emailsData)
        ? emailsData
        : Array.isArray(emailsData.data)
        ? emailsData.data
        : [];
      setEmails(emailsArray);
      setEmailsTotalPages(emailsData.pagination?.total_pages || 0);
    } catch (error) {}
  };

  // Function to fetch orders based on current selection
  const fetchOrders = async () => {
    if (!customer.id) return;

    try {
      let merchantId = null;
      let storeId = null;

      if (selectedLocationForOrders) {
        // Query for specific location
        storeId = selectedLocationForOrders;
      } else if (selectedMerchantForOrders) {
        // Query for all merchant locations
        merchantId = selectedMerchantForOrders;
      } else if (
        user.type === "saucey_admin" &&
        !selectedLocationForOrders &&
        !selectedMerchantForOrders
      ) {
        // Special case: saucey admin selected "All Locations"
      }

      // Format dates for API
      const startDateStr = startDate.toISOString().split("T")[0];
      const endDateStr = endDate.toISOString().split("T")[0];

      const response = await getOrdersByCustomer(
        customer.id,
        merchantId,
        storeId,
        ordersPage,
        ordersLimit,
        debouncedSearchQuery,
        startDateStr,
        endDateStr
      );
      // Update orders state with response data
      setOrders(response.data || []);
      setOrdersTotalPages(response.pagination?.total_pages || 0);
      setOrdersTotalRecords(response.pagination?.total || 0);
    } catch (error) {}
  };

  // Reset query when customer email (id) or location selection changes
  useEffect(() => {
    setOrdersPage(1);
    fetchOrders();
  }, [
    customer,
    selectedLocationForOrders,
    selectedMerchantForOrders,
    user.type,
    debouncedSearchQuery,
    startDate,
    endDate,
  ]);

  // Fetch orders when page or limit changes
  useEffect(() => {
    if (
      customerId &&
      (selectedLocationForOrders ||
        selectedMerchantForOrders ||
        user.type === "saucey_admin")
    ) {
      fetchOrders();
    }
  }, [
    ordersPage,
    ordersLimit,
    customerId,
    selectedLocationForOrders,
    selectedMerchantForOrders,
    user.type,
  ]);

  // Fetch activities when page or limit changes
  useEffect(() => {
    if (customer.mautic_id) {
      fetchActivities();
    }
  }, [activitiesPage, 25]);

  // Fetch emails when page or limit changes
  useEffect(() => {
    if (customer.mautic_id) {
      fetchEmails();
    }
  }, [emailsPage, 25]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Window resize listener to auto-expand on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isBasicInfoCollapsed) {
        setIsBasicInfoCollapsed(false);
      }
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Initial check
    handleResize();

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, [isBasicInfoCollapsed]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format date without timezone conversion (for birthdays)
  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    // Extract date parts directly from ISO string to avoid timezone conversion
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    // Fallback to regular formatDate if format doesn't match
    return formatDate(dateString);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      viewed_product: "Viewed Product",
      added_to_cart: "Added to Cart",
      started_checkout: "Started Checkout",
      email_opened: "Opened Email",
      email_clicked: "Clicked Email",
    };
    return labels[type] || type;
  };

  const getDeliveryTypeLabel = (type) => {
    const labels = {
      email_broadcast: "Email Broadcast",
      campaign: "Campaign",
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <InlineLoader message="Loading customer data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <div
      className={`${
        modalMode ? "px-0 py-0" : "px-4 sm:px-6 lg:px-8 py-8"
      } w-full max-w-9xl mx-auto`}
    >
      {/* Header */}
      {!modalMode && (
        <div className="mb-8">
          <button
            onClick={() => navigate("/crm/people")}
            className="flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 mb-4"
          >
            <FaArrowLeft className="mr-2" />
            Back to Customers
          </button>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                {customer.first_name} {customer.last_name}
              </h1>
              {/* <p className="text-slate-600 dark:text-slate-400 mt-1">
                Customer since {formatDate(customer.created_at)}
              </p> */}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column - Basic Info */}
        <div
          className={`${
            isBasicInfoCollapsed
              ? "lg:col-span-1"
              : "col-span-8 lg:col-span-5 min-[1100px]:col-span-4"
          } transition-all duration-300`}
        >
          <div
            className={`bg-white dark:bg-theme-surface-accent rounded-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 ${
              isBasicInfoCollapsed ? "p-2 w-16 mx-auto" : "p-6"
            }`}
          >
            <div
              className={`flex items-center  ${
                isBasicInfoCollapsed
                  ? "flex-row h-48 justify-center -rotate-90"
                  : "mb-4 justify-between"
              }`}
            >
              <h2
                className={`font-semibold text-slate-800 dark:text-slate-100 transition-all duration-300 ${
                  isBasicInfoCollapsed
                    ? "text-sm  whitespace-nowrap"
                    : "text-lg"
                }`}
              >
                Basic Information
              </h2>
              <button
                onClick={() => setIsBasicInfoCollapsed(!isBasicInfoCollapsed)}
                className="hidden lg:block p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                title={isBasicInfoCollapsed ? "Expand" : "Collapse"}
              >
                {isBasicInfoCollapsed ? (
                  <svg
                    className="w-3 h-3 text-slate-600 dark:text-slate-400 rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-slate-600 dark:text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                )}
              </button>
            </div>

            {!isBasicInfoCollapsed && (
              <>
                <CanView permissions="manager">
                  <div className="flex gap-3 mb-4">
                    {customer.banned ? (
                      <DangerButton
                        onClick={() => {
                          if (
                            user.type !== "saucey_admin" &&
                            customer.ban_info.global
                          ) {
                            dispatch(
                              addToast({
                                type: "error",
                                message:
                                  "You are not authorized to unban this customer.",
                                timer: 5,
                              })
                            );
                            return;
                          }
                          setUnbanModalOpen(true);
                        }}
                      >
                        <div className="flex items-center">
                          <FaBan className="w-4 h-4 mr-2" />
                          Unban Customer
                        </div>
                      </DangerButton>
                    ) : (
                      <DangerButton onClick={() => setBanModalOpen(true)}>
                        <div className="flex items-center">
                          <FaBan className="w-4 h-4 mr-2" />
                          Ban Customer
                        </div>
                      </DangerButton>
                    )}
                  </div>
                </CanView>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <FaEnvelope className="text-slate-400 mr-3 w-4 h-4" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Email
                      </p>
                      <p className="text-slate-800 dark:text-slate-100">
                        {customer.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <FaPhone className="text-slate-400 mr-3 w-4 h-4" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Phone
                      </p>
                      <p className="text-slate-800 dark:text-slate-100">
                        {formatPhoneNumber(customer.mobile)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <FaCakeCandles className="text-slate-400 mr-3 w-4 h-4" />
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Birthday
                      </p>
                      <p className="text-slate-800 dark:text-slate-100">
                        {formatDateOnly(customer.birthday)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-slate-700 mt-6 pt-6">
                  <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-6">
                    Spends
                  </h2>

                  <div className="space-y-6">
                    {/* Systemwide Stats */}
                    <CanView permissions="saucey_admin">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-5 border border-blue-100 dark:border-slate-600">
                        <div className="flex items-center gap-2 mb-4">
                          <FaCrown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <h3 className="text-md font-semibold text-slate-800 dark:text-slate-100">
                            Systemwide
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 min-[1200px]:grid-cols-1 min-[1500px]:grid-cols-12 gap-4">
                          <div className="min-[1500px]:col-span-3">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              Orders
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              {customer.overall_order_count || 0}
                            </p>
                          </div>
                          <div className="min-[1500px]:col-span-5">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              Spent
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              ${(customer.overall_total_spent || 0).toFixed(2)}
                            </p>
                          </div>
                          <div className="min-[1500px]:col-span-4">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              AOV
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              $
                              {customer.overall_total_spent
                                ? (
                                    customer.overall_total_spent /
                                    customer.overall_order_count
                                  ).toFixed(2)
                                : "0.00"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CanView>

                    {/* Across Merchant Locations */}
                    {selectedMerchant?.locations?.length > 1 && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-5 border border-green-100 dark:border-slate-600">
                        <div className="flex items-center gap-2 mb-4">
                          <h3 className="text-md font-semibold text-slate-800 dark:text-slate-100">
                            Across {selectedMerchant.name}'s locations
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 min-[1200px]:grid-cols-1 min-[1500px]:grid-cols-12 gap-4">
                          <div className="min-[1500px]:col-span-3">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              Orders
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              {customer.order_count_at_merchant || 0}
                            </p>
                          </div>
                          <div className="min-[1500px]:col-span-5">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              Spent
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              $
                              {customer.order_count_at_merchant
                                ? (
                                    customer.total_spent_at_merchant || 0
                                  ).toFixed(2)
                                : "0.00"}
                            </p>
                          </div>
                          <div className="min-[1500px]:col-span-4">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                              AOV
                            </p>
                            <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                              $
                              {customer.order_count_at_merchant
                                ? (
                                    customer.total_spent_at_merchant /
                                    customer.order_count_at_merchant
                                  ).toFixed(2)
                                : "0.00"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Current Location Stats */}
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-5 border border-purple-100 dark:border-slate-600">
                      {(selectedMerchant?.locations?.length > 1 ||
                        user.type === "saucey_admin") && (
                        <h3 className="text-md font-semibold text-slate-800 dark:text-slate-100 mb-4">
                          At {selectedLocation?.name || "this location"}
                        </h3>
                      )}
                      <div className="grid grid-cols-1 min-[1200px]:grid-cols-1 min-[1500px]:grid-cols-12 gap-4">
                        <div className="min-[1500px]:col-span-3">
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                            Orders
                          </p>
                          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                            {customer.order_count_at_location || 0}
                          </p>
                        </div>
                        <div className="min-[1500px]:col-span-5">
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                            Spent
                          </p>
                          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                            $
                            {(customer.total_spent_at_location || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="min-[1500px]:col-span-4">
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                            AOV
                          </p>
                          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                            $
                            {customer.order_count_at_location
                              ? (
                                  customer.total_spent_at_location /
                                  customer.order_count_at_location
                                ).toFixed(2)
                              : "0.00"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stats Section */}
                {/* <div className="border-t border-slate-200 dark:border-slate-700 mt-6 pt-6">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                    Stats
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Average Days Between Orders
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        {customer.order_count > 1
                          ? Math.round(
                              (new Date() - new Date(customer.created_at)) /
                                (1000 * 60 * 60 * 24) /
                                (customer.order_count - 1)
                            )
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Most Purchased Category
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        Premium Cigars
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Last Order Date
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        {orders.length > 0
                          ? formatDate(
                              orders[0].order_date || orders[0].updated_at
                            )
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Preferred Order Type
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        Pickup
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Email Read Rate
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        {deliveries.length > 0
                          ? Math.round(
                              (deliveries.filter((d) => d.opened).length /
                                deliveries.length) *
                                100
                            )
                          : 0}
                        %
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Email Click Rate
                      </p>
                      <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                        {deliveries.length > 0
                          ? Math.round(
                              (deliveries.filter((d) => d.clicked).length /
                                deliveries.length) *
                                100
                            )
                          : 0}
                        %
                      </p>
                    </div>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </div>

        {/* Right Column - Orders, Activities, Deliveries */}
        <div
          className={`${
            isBasicInfoCollapsed
              ? "lg:col-span-11"
              : "col-span-8 lg:col-span-7 min-[1100px]:col-span-8"
          } space-y-8 transition-all duration-300`}
        >
          {/* Combined Orders Section */}
          <div className="bg-white dark:bg-theme-surface-accent rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  {user.type === "saucey_admin" ||
                  selectedMerchant?.locations?.length > 1 ? (
                    <div className="flex items-center gap-2">
                      {ordersTotalRecords} Orders in
                      <select
                        className="ml-2 px-3 py-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={
                          selectedLocationForOrders ||
                          selectedMerchantForOrders ||
                          (user.type === "saucey_admin" ? "all-locations" : "")
                        }
                        onChange={(e) => {
                          const selectedValue = e.target.value;

                          if (selectedValue === "all-locations") {
                            // Special case for saucey admin - all locations across all merchants
                            setSelectedMerchantForOrders(null);
                            setSelectedLocationForOrders(null);
                            // TODO: Set a special flag for all locations query
                          } else {
                            // Check if it's a merchant ID or location ID
                            const isMerchant = merchants.some(
                              (m) => m.id === selectedValue
                            );

                            if (isMerchant) {
                              setSelectedMerchantForOrders(selectedValue);
                              setSelectedLocationForOrders(null); // Clear location selection
                            } else {
                              setSelectedLocationForOrders(selectedValue);
                              setSelectedMerchantForOrders(null); // Clear merchant selection
                            }
                          }
                        }}
                      >
                        {merchants.map((merchant) => (
                          <optgroup key={merchant.id} label={merchant.name}>
                            <option
                              key={`merchant-${merchant.id}`}
                              value={merchant.id}
                              className="font-semibold"
                            >
                              All {merchant.name} locations
                            </option>
                            {merchant.locations?.map((location) => (
                              <option
                                key={location.id}
                                value={location.id}
                                className="pl-4"
                              >
                                {location.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                        {user.type === "saucey_admin" && (
                          <optgroup key="saucey-admin" label="Saucey Admins">
                            <option
                              key="all-locations"
                              value="all-locations"
                              className="font-semibold"
                            >
                              All Locations
                            </option>
                          </optgroup>
                        )}
                      </select>
                    </div>
                  ) : (
                    "Orders" + ` (${ordersTotalRecords})`
                  )}
                </h2>

                {/* Filter Button */}
                <SecondaryButton
                  onClick={() => {
                    if (isFilterExpanded) {
                      // Clear search when collapsing
                      setSearchQuery("");
                      setDebouncedSearchQuery("");
                    }
                    setIsFilterExpanded(!isFilterExpanded);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <FaFilter className="w-4 h-4" />
                    Filter
                  </div>
                </SecondaryButton>
              </div>

              {/* Expandable Filter Bar */}
              {isFilterExpanded && (
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaMagnifyingGlass className="h-4 w-4 text-slate-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search by Order ID / Easy ID / Product Name"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="block w-full !pl-8 pr-10 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <DateSelect
                      defaultSelected={8}
                      onSelect={({ start, end }) => {
                        setStartDate(new Date(start));
                        setEndDate(new Date(end));
                        // TODO: Apply date filter to orders
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 dark:bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                      Total
                    </th>
                    {(user.type === "saucey_admin" ||
                      selectedMerchant?.locations?.length > 1) && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                        Location
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-theme-surface-accent divide-y divide-slate-200 dark:divide-slate-700">
                  {orders.length > 0 ? (
                    orders.map((order) => (
                      <tr
                        key={order.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                          <button
                            onClick={() => {
                              setSelectedOrderId(order.id);
                              setOrderModalOpen(true);
                            }}
                            className="text-sky-500 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 underline cursor-pointer"
                          >
                            {order.order_number}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {order.date_completed &&
                            order.date_completed !== "0001-01-01T00:00:00Z" && (
                              <div className="text-xs text-slate-400 mb-1">
                                Completed: {formatDate(order.date_completed)}
                              </div>
                            )}
                          <div>
                            {order.order_date &&
                            order.order_date !== "0001-01-01T00:00:00Z"
                              ? formatDate(order.order_date)
                              : formatDate(order.updated_at)}
                          </div>
                          {order.scheduled &&
                            order.scheduled !== "0001-01-01T00:00:00Z" && (
                              <div className="text-xs text-slate-400 mt-1">
                                Scheduled: {formatDate(order.scheduled)}
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            customColor={statusColor(
                              OrderStatusInverseEnum[order.order_status],
                              null
                            )}
                          >
                            {(
                              OrderStatusInverseEnum[order.order_status] ||
                              "Unknown"
                            )
                              .replace("_", " ")
                              .replace("IN PROGRESS", "EN ROUTE")}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                          {order.products
                            ? order.products.reduce(
                                (acc, item) => acc + (item.quantity || 0),
                                0
                              )
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${order.total?.toFixed(2) || "0.00"}
                        </td>
                        {(user.type === "saucey_admin" ||
                          selectedMerchant?.locations?.length > 1) && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                            <div className="flex items-center gap-2">
                              <span>
                                {order.vendor_populated?.name ||
                                  "Unknown Location"}
                              </span>
                              {order.vendor_populated?.direct === false && (
                                <Badge size="sm" color="purple">
                                  MP
                                </Badge>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          user.type === "saucey_admin" ||
                          selectedMerchant?.locations?.length > 1
                            ? 6
                            : 5
                        }
                        className="px-6 py-8 text-center text-slate-500 dark:text-slate-400"
                      >
                        No Orders Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {ordersTotalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                <PaginationNumeric
                  onSelect={(p) => setOrdersPage(p + 1)}
                  currentPage={ordersPage - 1}
                  totalPages={ordersTotalPages}
                  isFirstPage={ordersPage === 1}
                  isLastPage={ordersPage === ordersTotalPages}
                />
              </div>
            )}
          </div>

          {/* Recent Activities Section */}
          <CanView addons={["crm"]}>
            <div className="bg-white dark:bg-theme-surface-accent rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Recent Activities
                </h2>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {activities.length > 0 ? (
                    activities.map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          {getActivityIcon(activity.event_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                            {activity.event_type}
                          </p>
                          {activity.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0">
                              {activity.link ? (
                                <button
                                  onClick={() =>
                                    handleActivityLinkClick(activity.link)
                                  }
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                                >
                                  {activity.description}
                                </button>
                              ) : (
                                activity.description
                              )}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0">
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-slate-500 dark:text-slate-400 py-8">
                      No recent activities found
                    </div>
                  )}
                </div>

                {/* Activities Pagination */}
                {activitiesTotalPages > 1 && (
                  <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 mt-4">
                    <PaginationNumeric
                      onSelect={(p) => setActivitiesPage(p + 1)}
                      currentPage={activitiesPage - 1}
                      totalPages={activitiesTotalPages}
                      isFirstPage={activitiesPage === 1}
                      isLastPage={activitiesPage === activitiesTotalPages}
                    />
                  </div>
                )}
              </div>
            </div>
          </CanView>

          {/* Email Deliveries Section */}
          <CanView addons={["crm"]}>
            <div className="bg-white dark:bg-theme-surface-accent rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Email Deliveries
                </h2>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {emails.length > 0 ? (
                    emails.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {getEmailIcon(email.event_type)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                              {email.link ? (
                                <button
                                  onClick={() =>
                                    window.open(`${email.link}`, "_blank")
                                  }
                                  className="text-sky-500 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 underline cursor-pointer"
                                >
                                  {email.description}
                                </button>
                              ) : (
                                email.description
                              )}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                              {email.event_type.replace("blast", "broadcast")} •{" "}
                              {formatDateTime(email.sent_time)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-4">
                          <div className="flex flex-col items-end text-right">
                            <div className="flex items-center space-x-2">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  email.read ? "bg-green-500" : "bg-slate-300"
                                }`}
                              ></span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {email.read ? "Read" : "Not Read"}
                              </span>
                            </div>
                            {email.read && email.read_time && (
                              <span className="text-xs text-slate-400 dark:text-slate-500">
                                {formatDateTime(email.read_time)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-slate-500 dark:text-slate-400 py-8">
                      No email deliveries found
                    </div>
                  )}
                </div>

                {/* Emails Pagination */}
                {emailsTotalPages > 1 && (
                  <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 mt-4">
                    <PaginationNumeric
                      onSelect={(p) => setEmailsPage(p + 1)}
                      currentPage={emailsPage - 1}
                      totalPages={emailsTotalPages}
                      isFirstPage={emailsPage === 1}
                      isLastPage={emailsPage === emailsTotalPages}
                    />
                  </div>
                )}
              </div>
            </div>
          </CanView>
        </div>
      </div>

      {/* Past Order Modal */}
      <PastOrderModal
        modalOpen={orderModalOpen}
        setModalOpen={setOrderModalOpen}
        orderID={selectedOrderId}
      />

      {/* Ban Customer Modal */}
      {banModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <FaTriangleExclamation className="w-6 h-6 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                  Ban Customer
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Are you sure you want to ban{" "}
                <strong>
                  {customer.first_name} {customer.last_name}
                </strong>
                ? This action will prevent them from placing orders and
                accessing the platform.
              </p>

              <div className="mb-4">
                <label
                  htmlFor="ban-reason"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >
                  Reason for banning *
                </label>
                <textarea
                  id="ban-reason"
                  rows={3}
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Please provide a reason for banning this customer..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <CanView permissions="saucey_admin">
                <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <ToggleSwitch
                    checked={banGlobally}
                    onChange={setBanGlobally}
                    label="Ban Globally"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Ban Globally
                      <FaCrown className="w-3 h-3 inline ml-1 text-amber-500" />
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Ban this customer across all merchants and locations
                    </div>
                  </div>
                </div>
              </CanView>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton
                onClick={() => {
                  setBanModalOpen(false);
                  setBanReason("");
                  setBanGlobally(false);
                }}
                disabled={banningCustomer}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleBanCustomer}
                loading={banningCustomer}
                holdToContinue={true}
                disabled={!banReason.trim()}
              >
                Ban Customer
              </DangerButton>
            </div>
          </div>
        </div>
      )}

      {/* Unban Customer Modal */}
      {unbanModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <FaTriangleExclamation className="w-6 h-6 text-green-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                  Unban Customer
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Are you sure you want to unban{" "}
                <strong>
                  {customer.first_name} {customer.last_name}
                </strong>
                ? This action will restore their access to place orders and use
                the platform.
              </p>

              {customer.ban_info && (
                <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Ban Information:
                  </h4>
                  <div className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <p>
                      <strong>Banned on:</strong>{" "}
                      {new Date(
                        customer.ban_info.created_at
                      ).toLocaleDateString()}
                    </p>
                    {customer.ban_info.notes &&
                      customer.ban_info.notes.length > 0 && (
                        <p>
                          <strong>Reason:</strong>{" "}
                          {customer.ban_info.notes.join(", ")}
                        </p>
                      )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton
                onClick={() => setUnbanModalOpen(false)}
                disabled={unbanningCustomer}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleUnbanCustomer}
                loading={unbanningCustomer}
                holdToContinue={true}
              >
                Unban Customer
              </DangerButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Return content directly if not in modal mode, otherwise return the content for modal wrapper
  return content;
};

export default CustomerDetail;
